/**
 * FR-018.7 R2 — why Confirm is off when the preview would supersede a row the editor does not restate,
 * and what to do: the label is already another object's (named, with a link there), or this object
 * holds another row for it (change that one first) — "frühere Jahre" only when that row provably starts
 * before the item's season. An id the preview names no owner for
 * (an older backend) keeps the generic reload sentence.
 */
import React from 'react';
import type { PreviewResponse } from '../../../../services/sourcesService';
import type { Translate } from './previewSentences';
import { unrestatedOwners } from './supersedeGuard';

interface UnrestatedNoticeProps {
    preview: PreviewResponse;
    accounted: readonly string[];
    entityType: string;
    entityId: string;
    t: Translate;
    onNavigate?(entityType: string, entityId: string): void;
}

export const UnrestatedNotice: React.FC<UnrestatedNoticeProps> = ({ preview, accounted, entityType, entityId, t, onNavigate }) => {
    const owners = unrestatedOwners(preview, new Set(accounted), { entityType, entityId });
    return (
        <>
            {owners.elsewhere.map((o) => {
                const name = o.target_name;
                return (
                    <React.Fragment key={`${o.target_type}:${o.target_ref}`}>
                        <p className="mapping-editor__reason" role="alert">
                            {name
                                ? t(
                                      'farm.sources.warnings.unrestatedElsewhere',
                                      'Dieser Name ist bereits „{{name}}“ zugeordnet. Ändern Sie die Zuordnung dort.',
                                      { name },
                                  )
                                : t(
                                      'farm.sources.warnings.unrestatedElsewhereUnnamed',
                                      'Dieser Name ist bereits einem anderen Objekt zugeordnet. Ändern Sie die Zuordnung dort.',
                                  )}
                        </p>
                        {onNavigate && o.target_kind === 'entity' && o.target_type && o.target_ref && (
                            <button
                                type="button"
                                className="mapping-editor__link"
                                onClick={() => onNavigate(o.target_type as string, o.target_ref as string)}
                            >
                                {name
                                    ? t('farm.sources.actions.goToOwner', 'Zu „{{name}}“ wechseln', { name })
                                    : t('farm.sources.actions.goToOwnerUnnamed', 'Zum Objekt wechseln')}
                            </button>
                        )}
                    </React.Fragment>
                );
            })}
            {owners.here && (
                <p className="mapping-editor__reason" role="alert">
                    {owners.hereEarlier
                        ? t(
                              'farm.sources.warnings.unrestatedHere',
                              'Für diesen Namen gibt es hier bereits eine Zuordnung für frühere Jahre – ändern Sie zuerst diese.',
                          )
                        : t(
                              'farm.sources.warnings.unrestatedHereOther',
                              'Für diesen Namen gibt es hier bereits eine andere Zuordnung – ändern Sie zuerst diese.',
                          )}
                </p>
            )}
            {owners.unknown && (
                <p className="mapping-editor__reason" role="alert">
                    {t(
                        'farm.sources.warnings.unrestated_rows',
                        'Diese Änderung würde weitere Zuordnungen desselben Namens ersetzen, die hier nicht angezeigt werden. Bitte Seite neu laden und erneut prüfen.',
                    )}
                </p>
            )}
        </>
    );
};
